/**
 * Per-template voice settings — keep field semantics aligned with supabase/functions/_shared/agent_voice_config.ts
 */

export type AgentTtsProvider = 'openai' | 'elevenlabs';

export interface AgentVoiceConfig {
  tts_provider?: AgentTtsProvider;
  tts_voice?: string;
  tts_speed?: number;
  tts_instructions?: string;
  elevenlabs_voice_id?: string;
  elevenlabs_language_code?: string | null;
  stt_locale?: string | null;
}

export const OPENAI_TTS_VOICE_OPTIONS: { id: string; label: string }[] = [
  { id: 'alloy', label: 'Alloy — neutral, balanced' },
  { id: 'ash', label: 'Ash — clear, articulate' },
  { id: 'ballad', label: 'Ballad — smooth, melodic' },
  { id: 'coral', label: 'Coral — warm, friendly' },
  { id: 'echo', label: 'Echo — resonant, clear' },
  { id: 'fable', label: 'Fable — expressive, storytelling' },
  { id: 'nova', label: 'Nova — energetic, engaging' },
  { id: 'onyx', label: 'Onyx — deep, authoritative' },
  { id: 'sage', label: 'Sage — calm, measured' },
  { id: 'shimmer', label: 'Shimmer — light, cheerful' },
  { id: 'verse', label: 'Verse — poetic, rhythmic' },
  { id: 'marin', label: 'Marin — natural, conversational' },
  { id: 'cedar', label: 'Cedar — warm, grounded' },
];

const OPENAI_VOICE_SET = new Set(OPENAI_TTS_VOICE_OPTIONS.map((o) => o.id));

export const STT_LOCALE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Auto detect (recommended)' },
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-IN', label: 'English (India)' },
  { value: 'hi-IN', label: 'Hindi (India)' },
  { value: 'ta-IN', label: 'Tamil (India)' },
  { value: 'te-IN', label: 'Telugu (India)' },
  { value: 'bn-IN', label: 'Bengali (India)' },
  { value: 'mr-IN', label: 'Marathi (India)' },
  { value: 'gu-IN', label: 'Gujarati (India)' },
  { value: 'kn-IN', label: 'Kannada (India)' },
  { value: 'ml-IN', label: 'Malayalam (India)' },
  { value: 'pa-IN', label: 'Punjabi (India)' },
];

export function normalizeOpenAiVoiceForForm(v: string | undefined): string {
  const s = (v ?? '').trim().toLowerCase();
  if (s && OPENAI_VOICE_SET.has(s)) return s;
  return 'alloy';
}

export function clampTtsSpeed(n: number | undefined): number {
  if (n === undefined || !Number.isFinite(n)) return 1;
  return Math.min(4, Math.max(0.25, n));
}

const ELEVEN_VOICE_ID_RE = /^[a-zA-Z0-9_-]{4,128}$/;

export function isValidElevenlabsVoiceIdInput(id: string): boolean {
  return ELEVEN_VOICE_ID_RE.test(id.trim());
}

/** Voice config persisted when voice is enabled; `{}` when disabled is fine */
export function buildVoiceConfigForSave(params: {
  voiceEnabled: boolean;
  ttsProvider: AgentTtsProvider;
  ttsVoice: string;
  ttsSpeed: number;
  ttsInstructions: string;
  elevenlabsVoiceId: string;
  elevenlabsLanguageCode: string;
  sttLocale: string;
}): AgentVoiceConfig | Record<string, never> {
  if (!params.voiceEnabled) return {};
  const tts_voice = normalizeOpenAiVoiceForForm(params.ttsVoice);
  const tts_speed = clampTtsSpeed(params.ttsSpeed);
  const tts_instructions = params.ttsInstructions.trim().slice(0, 4000);
  const elevenlabs_voice_id = params.elevenlabsVoiceId.trim();
  const elRaw = params.elevenlabsLanguageCode.trim().toLowerCase();
  const elevenlabs_language_code =
    elRaw.length >= 2 && elRaw.length <= 12 && /^[a-z]{2,3}(-[a-z]{2,3})?$/i.test(elRaw) ? elRaw : null;
  const stt_locale = params.sttLocale.trim() ? params.sttLocale.trim().slice(0, 32) : null;
  const base: AgentVoiceConfig = {
    tts_provider: params.ttsProvider,
    tts_voice,
    tts_speed,
    tts_instructions: tts_instructions || undefined,
    stt_locale,
    ...(params.ttsProvider === 'elevenlabs'
      ? {
          elevenlabs_voice_id,
          ...(elevenlabs_language_code ? { elevenlabs_language_code } : { elevenlabs_language_code: null }),
        }
      : {}),
  };
  return base;
}

export function parseVoiceConfigFromRow(raw: unknown): {
  ttsProvider: AgentTtsProvider;
  ttsVoice: string;
  ttsSpeed: number;
  ttsInstructions: string;
  elevenlabsVoiceId: string;
  elevenlabsLanguageCode: string;
  sttLocale: string;
} {
  const o = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const ttsProvider: AgentTtsProvider = o.tts_provider === 'elevenlabs' ? 'elevenlabs' : 'openai';
  return {
    ttsProvider,
    ttsVoice: normalizeOpenAiVoiceForForm(typeof o.tts_voice === 'string' ? o.tts_voice : ''),
    ttsSpeed: clampTtsSpeed(typeof o.tts_speed === 'number' ? o.tts_speed : Number(o.tts_speed)),
    ttsInstructions: typeof o.tts_instructions === 'string' ? o.tts_instructions : '',
    elevenlabsVoiceId: typeof o.elevenlabs_voice_id === 'string' ? o.elevenlabs_voice_id : '',
    elevenlabsLanguageCode:
      typeof o.elevenlabs_language_code === 'string' && o.elevenlabs_language_code
        ? o.elevenlabs_language_code
        : '',
    sttLocale: typeof o.stt_locale === 'string' ? o.stt_locale : '',
  };
}

export function templateUsesElevenlabsTts(voiceConfig: AgentVoiceConfig | null | undefined): boolean {
  return voiceConfig?.tts_provider === 'elevenlabs';
}

/** BCP-47 for Whisper/Web Speech; undefined = auto (omit param) */
export function effectiveSttLocale(template: { voice_config?: AgentVoiceConfig | null }): string | undefined {
  const v = template.voice_config?.stt_locale;
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  return t || undefined;
}
